import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mergeDeep, omit } from 'remeda';
import {
  FullEditorialAsset,
  PatchType,
  PostCoreUploadResult,
  ResourceType,
  UserAccountSummary,
} from 'utility/types/dataProvider';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';
import { AxiosProgressEvent } from 'axios';
import { useNotify, useDataProvider } from 'react-admin';
import { useDropzone, DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { useMutation, useQuery, useMutationState } from '@tanstack/react-query';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosRuntime } from 'providers/contexts/CosmosRuntimeContext';
import { truncateString } from 'utility/functions';
import { hasField59Upload } from 'utility/privileges';
import { hasBetaProgram } from 'utility/experiments';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { isVideoFile, uploadVideoToVms } from 'utility/vmsUpload';
import { WireImportStatusData } from 'providers/contexts/extensions/Wire';
import { TBatchImportUploadPresetsForm } from './components/BatchImportUploadPresets';
import { useForm, FormProvider } from 'react-hook-form';
import { useAppSettings } from 'providers/contexts/SettingsContext';
import { createPatchOperationsPayload, getIsBpmFile } from './utils/functions';
import { useTranslation } from 'react-i18next';
import {
  normalizeWorkflowId,
  useValidatedDefaultWorkflowId,
} from 'utility/workflowUtils';

// --------------------------------------------------------------
// Schemas / Validation
// --------------------------------------------------------------

const RESOURCE_TYPES = [
  'editorial',
  'wire',
  'user',
  'subscription',
] as const satisfies readonly ResourceType[];

const NxtImportItemSchema = z.object({
  importId: z.string(),
  binaryId: z.string().optional(),
  uuid: z.string().optional(),
  siteId: z.string(),
  timestamp: z.number(),
  resourceType: z.enum(RESOURCE_TYPES),
  progress: z.number(),
  isQueued: z.boolean(),
  isUploading: z.boolean(),
  isProcessing: z.boolean(),
  isReady: z.boolean(),
  isFailed: z.boolean(),
  isCanceled: z.boolean(),
  abortController: z.any().optional(),
  file: z.any().optional(),
  importFile: z.boolean().optional(),
  fileName: z.string().optional(),
  runtimeId: z.string().optional(),
  type: z.string().optional(),
  misc: z.any().optional(),
  data: z.any().optional(),
  groupId: z.string().optional(),
  isPendingBatch: z.boolean().optional(),
  batchGroupOrder: z.number().optional(),
  notificationSent: z.boolean().optional(),
  inlineAsset: z.boolean().optional(),
});

// --------------------------------------------------------------
// Types
// --------------------------------------------------------------

export type NxtImportItem = {
  importId: string;
  binaryId?: string;
  uuid?: string;
  siteId: string;
  timestamp: number;
  resourceType: ResourceType;
  progress: number;
  isQueued: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  isReady: boolean;
  isFailed: boolean;
  isCanceled: boolean;
  abortController?: AbortController;
  file?: File;
  importFile?: boolean;
  fileName?: string;
  /** Tab-scoped identifier: only the tab that created this import will process it. */
  runtimeId?: string;
  misc?: Record<string, any>;
  type?: FullEditorialAsset['type'];
  data?: Partial<FullEditorialAsset> | null;
  groupId?: string;
  isPendingBatch?: boolean;
  batchGroupOrder?: number;
  /** Persisted flag — prevents duplicate push notifications after page reload. */
  notificationSent?: boolean;
  inlineAsset?: boolean;
};

type NxtImportStorageSlice = {
  imports: Record<string, NxtImportItem>;
};

type NxtImportStorageData = {
  [siteKey: string]: NxtImportStorageSlice;
};

export type NxtImportContextType = {
  isDragActive: boolean;
  imports: Record<string, NxtImportItem>;
  addImport: (
    importId: string,
    resourceType: ResourceType,
    file?: File,
    inline?: boolean,
    groupId?: string,
    isPendingBatch?: boolean,
    batchGroupOrder?: number,
  ) => void;
  updateImport: (importId: string, updates: Partial<NxtImportItem>) => void;
  removeImport: (importId: string) => void;
  cancelImport: (importId: string) => void;
  maybeWireImport: (
    record: any,
    channel_id: string,
    channel_name: string | null,
  ) => Promise<string | boolean | undefined>;
  clearAllImports: () => void;
  importCount: number;
  readyCount: number;
  hasActiveImports: boolean;
  hasReadyImports: boolean;
  allImportsReady: boolean;
  hasImportErrors: boolean;
  hasSyndicationImports: boolean;
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  toggleImportModal: () => void;
  importModalOpen: boolean;
  accordionExpanded: boolean;
  setAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
};

// type is mantines hook argument "StorageProperties<T>"
// but was unable to import it here it directly from mantine
type StorageProps<T> = Parameters<typeof useLocalStorage<T>>[0];

type TWireImportWithStatus = WireImportStatusData & {
  object_id?: string;
};

const STATUS_KEYS = [
  'isQueued',
  'isUploading',
  'isProcessing',
  'isReady',
  'isFailed',
  'isCanceled',
] as const;

/**
 * Keep status flags mutually exclusive so partial updates don't leave stale booleans
 * behind after deep merges (for example: isReady=true with isQueued still true).
 */
function normalizeImportStatuses(item: Partial<NxtImportItem>): Partial<NxtImportItem> {
  // Normalized resets all to false initially
  const normalized = {
    ...item,
    isQueued: false,
    isUploading: false,
    isProcessing: false,
    isReady: false,
    isFailed: false,
    isCanceled: false,
  };

  // Check original item properties, set that status on normalized and return...
  if (item.isCanceled) {
    normalized.isCanceled = true;
    return normalized;
  }
  if (item.isFailed) {
    normalized.isFailed = true;
    return normalized;
  }
  if (item.isReady) {
    normalized.isReady = true;
    return normalized;
  }
  if (item.isUploading) {
    normalized.isUploading = true;
    return normalized;
  }
  if (item.isProcessing) {
    normalized.isProcessing = true;
    return normalized;
  }
  if (item.isQueued) {
    normalized.isQueued = true;
    return normalized;
  }

  return normalized;
}

function hasStatusUpdate(updates: Partial<NxtImportItem>): boolean {
  return STATUS_KEYS.some((key) => key in updates);
}

// --------------------------------------------------------------
// Constants & Helpers
// --------------------------------------------------------------

const NXT_IMPORT_LS_KEY = 'nxtImportStorage';
const IMPORT_RESTORED_LS_KEY = 'isRestoredImportSession';
const IMPORT_LAST_SITE_KEY = 'lastActiveImportSite';
const IMPORT_AUTOHIDE_TIME = 24 * 60 * 60 * 1000; // 24h
const MAX_CONCURRENT_UPLOADS = 5;
const DEFAULT_STORAGE_DATA: NxtImportStorageData = {};
/**
 * sync must stay false: @mantine/hooks' useLocalStorage sync:true echoes every
 * write back through a same-tab CustomEvent that re-applies a snapshot of the
 * updater async. With the high write frequency here (progress ticks, status
 * transitions) that echo can clobber a just-applied status update with a stale
 * one, which is why "uploading" was visually skipped (stuck on "queued" until
 * "processing"). See @mantine/hooks create-storage.mjs setStorageValue.
 */
const STORAGE_HOOK_CONFIG: StorageProps<NxtImportStorageData> = {
  key: NXT_IMPORT_LS_KEY,
  defaultValue: DEFAULT_STORAGE_DATA,
  sync: false,
};

/**
 * Read the data from localStorage without subscribing to it.
 * Avoids unnecessary re-renders / useEffect loops.
 */
function getNxtImportStorage(): NxtImportStorageData {
  const storage = readLocalStorageValue<NxtImportStorageData>(STORAGE_HOOK_CONFIG);
  return storage;
}

// --------------------------------------------------------------
// Hooks and Context Provider
// --------------------------------------------------------------
export const NxtImportContext = createContext<NxtImportContextType | undefined>(
  undefined,
);

export const useNxtImport = (): NxtImportContextType => {
  const ctx = useContext(NxtImportContext);
  if (!ctx) {
    throw new Error('useNxtImport must be used within a NxtImportProvider');
  }
  return ctx;
};

interface NxtImportProviderProps {
  children: ReactNode;
}

export const NxtImportProvider = memo(({ children }: NxtImportProviderProps) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { activeSite, userInfo, sessionConfig } = useCosmos();
  const { runtimeId } = useCosmosRuntime();
  const betaProgramEnabled = hasBetaProgram(sessionConfig, userInfo);
  const hasField59 = hasField59Upload(sessionConfig);
  const {
    appSettings: { site: siteSettings, global: globalSettings },
  } = useAppSettings();

  // Validate the stored default workflow ID against the API before using it.
  const candidateDefaultWorkflowId = normalizeWorkflowId(
    siteSettings?.user?.default_workflow,
  );
  const {
    effectiveWorkflowId: effectiveDefaultWorkflowId,
    isPending: isDefaultWorkflowPending,
  } = useValidatedDefaultWorkflowId(candidateDefaultWorkflowId);

  // batch import local storage state
  const [batchImportStorage, setNxtImportStorage] =
    useLocalStorage<NxtImportStorageData>(STORAGE_HOOK_CONFIG);
  const [isDragActive, setIsDragActive] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const toggleImportModal = useCallback(() => {
    setImportModalOpen((prev) => !prev);
  }, []);
  // Control for whether the Batch Import presets accordion is expanded
  const [accordionExpanded, setAccordionExpanded] = useState<boolean>(false);

  const currentUserUuid: UserAccountSummary['uuid'] = useMemo(() => {
    const { uuid = '' } = userInfo || {};
    return uuid;
  }, [userInfo]);

  // Initialize the form for batch upload settings
  const { reset, ...batchUploadPresetsForm } = useForm<TBatchImportUploadPresetsForm>({
    defaultValues: {
      workflow_id: '',
      workflow_process_id: '',
      authors:
        !!(siteSettings?.user?.default_author ?? globalSettings?.user?.default_author) &&
        currentUserUuid
          ? [currentUserUuid]
          : [],
      publish_status: '',
      keywords: [],
      slug: '',
      sections: [],
      is_publish_status_disabled_by_workflow:
        siteSettings?.user?.is_first_process_force_draft ?? false,
    },
  });

  // normalize active site key for consistent storage access
  const siteKey = useMemo(() => activeSite?.toLowerCase()?.trim(), [activeSite]);

  // Update batchUploadPresetsForm when siteSettings or currentUser changes
  const prevSettingsRef = useRef({
    workflow: '',
    forceDraft: false,
    uuid: '',
    defaultAuthor: false,
  });
  useEffect(() => {
    // Wait for the async workflow ID validation to complete before resetting.
    if (isDefaultWorkflowPending) return;

    const defaultWorkflow = effectiveDefaultWorkflowId || '';
    const defaultProcessIsForceDraft =
      siteSettings?.user?.is_first_process_force_draft ?? false;
    const defaultAuthor = !!(
      siteSettings?.user?.default_author ?? globalSettings?.user?.default_author
    );
    const authors = defaultAuthor && currentUserUuid ? [currentUserUuid] : [];

    // Only reset if values actually changed
    if (
      prevSettingsRef.current.workflow !== defaultWorkflow ||
      prevSettingsRef.current.forceDraft !== defaultProcessIsForceDraft ||
      prevSettingsRef.current.uuid !== currentUserUuid ||
      prevSettingsRef.current.defaultAuthor !== defaultAuthor
    ) {
      prevSettingsRef.current = {
        workflow: defaultWorkflow,
        forceDraft: defaultProcessIsForceDraft,
        uuid: currentUserUuid,
        defaultAuthor,
      };

      reset({
        workflow_id: defaultWorkflow,
        workflow_process_id: '',
        authors,
        publish_status: '',
        keywords: [],
        slug: '',
        sections: [],
        is_publish_status_disabled_by_workflow: defaultProcessIsForceDraft,
      });
    }
  }, [
    siteSettings,
    currentUserUuid,
    reset,
    siteSettings?.user?.default_author,
    globalSettings?.user?.default_author,
    effectiveDefaultWorkflowId,
    isDefaultWorkflowPending,
  ]);

  // Tracks whether the prune-on-mount effect has already run for the current siteKey.
  // Guards against re-running when setNxtImportStorage is an unstable reference.
  const prunedSiteKeyRef = useRef<string | null>(null);

  // on site switch / app load prune expired imports
  useEffect(() => {
    if (!siteKey) return;
    if (prunedSiteKeyRef.current === siteKey) {
      // Guard is working — effect re-ran (likely due to setNxtImportStorage reference
      // changing) but pruning is correctly skipped. If this log appears frequently
      // during a single session it confirms the setter reference was unstable.
      return;
    }
    prunedSiteKeyRef.current = siteKey;

    // read without subscribing to avoid effect loops
    const full = getNxtImportStorage();
    // get the imports slice for this site
    const siteImports = full?.[siteKey]?.imports ?? {};

    // filter (autohide) expired imports by timestamp
    // keep only those that are still valid
    const now = Date.now();
    const validImports: Record<string, NxtImportItem> = {};
    for (const [id, item] of Object.entries(siteImports)) {
      // Skip expired imports
      if (now >= item?.timestamp + IMPORT_AUTOHIDE_TIME) {
        continue;
      }

      /**
       * Mark uploads that were in progress as failed, typically on page reload
       * or when the page was closed during upload. The abort controller is lost
       * on page reload so we can't resume them.
       */
      if (item.isUploading || item.isQueued) {
        validImports[id] = {
          ...item,
          siteId: siteKey,
          isUploading: false,
          isQueued: false,
          isFailed: true,
          abortController: undefined, // Clear the lost abort controller
          misc: {
            errorMessage: 'Upload interrupted. Please try again.',
          },
        };
      } else if (item.isPendingBatch) {
        // Pending batch items lose their sequential queue order on reload;
        // mark them as failed so the user knows to re-upload.
        validImports[id] = {
          ...item,
          siteId: siteKey,
          isPendingBatch: false,
          isQueued: false,
          isFailed: true,
          misc: {
            errorMessage: 'Batch upload interrupted. Please try again.',
          },
        };
      } else {
        // For items that weren't actively uploading, keep them as-is
        validImports[id] = { ...item, siteId: siteKey };
      }
    }

    // update the imports map with just valid items
    setNxtImportStorage((prev) => ({
      ...prev,
      [siteKey]: { imports: validImports },
    }));

    // manage restored session flags
    const lastSiteLS = localStorage.getItem(IMPORT_LAST_SITE_KEY);
    const hasValidImports = Boolean(Object.keys(validImports).length);
    // if we have valid imports, set the restored session flag
    // and update the last active site key
    if (hasValidImports) {
      localStorage.setItem(IMPORT_RESTORED_LS_KEY, 'true');
      localStorage.setItem(IMPORT_LAST_SITE_KEY, siteKey);
    }
    // if no valid imports and this was the last site
    // clear the restored session flags
    if (!hasValidImports && lastSiteLS === siteKey) {
      localStorage.removeItem(IMPORT_RESTORED_LS_KEY);
      localStorage.removeItem(IMPORT_LAST_SITE_KEY);
    }
  }, [siteKey, setNxtImportStorage]);

  // this sites imports derived from the batchImportStorage
  // sorted from newest to oldest based on timestamp
  const imports = useMemo(() => {
    const siteImports = batchImportStorage?.[siteKey]?.imports ?? {};
    const sortedEntries = Object.entries(siteImports).sort(
      ([, a], [, b]) => b.timestamp - a.timestamp,
    );
    return Object.fromEntries(sortedEntries);
  }, [batchImportStorage, siteKey]);

  // --- Actions handlers --------------------------------------------------

  /**
   * Add a new export item to the context.
   */
  const addImport = useCallback(
    (
      importId: string,
      resourceType: ResourceType,
      file?: File,
      inline?: boolean,
      groupId?: string,
      isPendingBatch?: boolean,
      batchGroupOrder?: number,
    ) => {
      const now = Date.now();
      const newItem: NxtImportItem = {
        importId: importId,
        siteId: siteKey,
        timestamp: now,
        resourceType: resourceType,
        runtimeId: runtimeId,
        progress: 0,
        isQueued: true,
        isUploading: false,
        isProcessing: false,
        isReady: false,
        isFailed: false,
        isCanceled: false,
      };

      if (file) {
        newItem.file = file;
        newItem.fileName = file.name;
        if (getIsBpmFile(file)) {
          newItem.importFile = true;
        }
      }

      if (inline) {
        // Publish is forced per-upload via a patch operation (see fileBinaryUploadMutation);
        // do not mutate the shared batch upload presets form here.
        newItem.inlineAsset = true;
      }

      if (groupId) {
        newItem.groupId = groupId;
      }

      if (isPendingBatch) {
        newItem.isPendingBatch = true;
        newItem.isQueued = false; // Pending batch items wait for their turn
      }

      if (batchGroupOrder !== undefined) {
        newItem.batchGroupOrder = batchGroupOrder;
      }

      // merge in the new export
      setNxtImportStorage((prev) => {
        const merged = mergeDeep(prev, {
          [siteKey]: { imports: { [importId]: newItem } },
        });
        return merged;
      });
    },
    [siteKey, runtimeId, setNxtImportStorage],
  );

  /**
   * Update an existing imports fields with new data.
   */
  const updateImport = useCallback(
    (importId: string, updates: Partial<NxtImportItem>) => {
      setNxtImportStorage((prev) => {
        // Get the current imports from the storage state, not the derived imports
        const currentImports = prev?.[siteKey]?.imports ?? {};
        const original = currentImports[importId];

        // nothing to update
        if (!original) {
          return prev;
        }

        // merge existing and new updates
        const merged = mergeDeep(original, updates);
        const mergedWithNormalizedStatus = hasStatusUpdate(updates)
          ? normalizeImportStatuses(merged)
          : merged;
        // validate the merge result against the schema
        const parseResult = NxtImportItemSchema.safeParse(mergedWithNormalizedStatus);
        if (!parseResult.success) {
          console.warn('[updateImport] Update failed validation:', parseResult?.error);
          return prev;
        }

        // if validation passed, we have a valid updated item
        const validNxtImportItem = parseResult?.data;
        // return updated storage
        return mergeDeep(prev, {
          [siteKey]: { imports: { [importId]: validNxtImportItem } },
        });
      });
    },
    [siteKey, setNxtImportStorage],
  );

  /**
   * Remove an export by its ID.
   */
  const removeImport = useCallback(
    (importId: string) => {
      setNxtImportStorage((prev) => {
        const current = prev?.[siteKey]?.imports ?? {};
        const rest = omit(current, [importId]);
        return {
          ...prev,
          [siteKey]: { imports: rest },
        };
      });
    },
    [siteKey, setNxtImportStorage],
  );

  /**
   * Cancel an import and abort any ongoing upload.
   */
  const cancelImport = useCallback(
    (importId: string) => {
      const importItem = imports[importId];
      if (!importItem) return;

      // If there's an active upload, abort it
      if (importItem.abortController && (importItem.isUploading || importItem.isQueued)) {
        try {
          importItem.abortController.abort();
        } catch (error) {
          console.error(`Failed to abort import ${importId}:`, error);
        }
        updateImport(importId, {
          isCanceled: true,
          isUploading: false,
          isQueued: false,
        });
      } else {
        // Otherwise just remove it from the list
        removeImport(importId);
      }
    },
    [imports, removeImport, updateImport],
  );

  /**
   * Clear all imports for the active site.
   */
  const clearAllImports = useCallback(() => {
    setNxtImportStorage((prev) => ({
      ...prev,
      [siteKey]: { imports: {} },
    }));
  }, [siteKey, setNxtImportStorage]);

  // --- Computed context values ------------------------------------------

  const importCount = Object.keys(imports).length;
  const readyCount = Object.values(imports).filter((e) => e.isReady).length;
  const hasSyndicationImports =
    Object.values(imports).filter((e) => e.resourceType === 'wire').length > 0;
  const hasActiveImports =
    Object.values(imports).filter((e) => e.isProcessing || e.isUploading).length > 0;
  const hasReadyImports = readyCount > 0;
  const hasImportErrors = Object.values(imports).filter((e) => e.isFailed).length > 0;
  const allImportsReady = hasActiveImports && readyCount === importCount;

  // Create stable query key for wire imports
  const wireImportIds = useMemo(() => {
    return Object.values(imports)
      .filter((e) => e.resourceType === 'wire' && !e.isReady && e.isProcessing)
      .map((e) => e.importId)
      .sort();
  }, [imports]);

  const { data: wireImportStatusData } = useQuery({
    queryKey: ['wireImportStatus', siteKey, wireImportIds],
    queryFn: async () => {
      // Filter imports that need status checking
      const syndicationImports = Object.values(imports).filter(
        (e) => e.resourceType === 'wire' && !e.isReady && e.isProcessing,
      );

      // Group object IDs by channel ID
      const channelGroupedImports: Record<string, string[]> = {};

      syndicationImports.forEach((importItem) => {
        const importIdParts = importItem.importId.split(':');
        if (importIdParts.length >= 2) {
          const channel_id = importIdParts[0];
          const object_id = importIdParts.slice(1).join(':'); // Handle case where object_id might contain colons

          if (!channelGroupedImports[channel_id]) {
            channelGroupedImports[channel_id] = [];
          }
          channelGroupedImports[channel_id].push(object_id);
        }
      });

      // Make one API call per channel with all object IDs for that channel
      const statuses = await Promise.all(
        Object.entries(channelGroupedImports).map(async ([channel_id, object_ids]) => {
          try {
            const response = await dataProvider.getWireImportStatus(
              channel_id,
              object_ids,
            );
            const statusData = response?.data || null;
            return { channel_id, statusData: statusData }; // Return the status data for the channel
          } catch (error) {
            console.error(`Error fetching status for channel_id ${channel_id}:`, error);
            return { channel_id, statusData: null }; // Handle error gracefully
          }
        }),
      );
      return statuses; // Return the array of statuses for all channels
    },
    enabled: wireImportIds.length > 0,
    refetchInterval: 5000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0, // Don't cache completed queries
    staleTime: 4000, // Consider data stale after 4s (before next refetch)
  });

  useEffect(() => {
    if (wireImportStatusData) {
      // Get the current imports from storage to avoid dependency on imports state
      const currentImports = getNxtImportStorage()?.[siteKey]?.imports ?? {};

      // Update imports based on the wire import status data
      wireImportStatusData.forEach(({ channel_id, statusData }) => {
        if (statusData && Array.isArray(statusData)) {
          statusData.forEach((statusItem: TWireImportWithStatus) => {
            // Find the matching import item by reconstructing the importId
            const matchingImportId = `${channel_id}:${
              statusItem.uuid || statusItem.object_id
            }`;
            const matchingImport = currentImports[matchingImportId];

            if (matchingImport) {
              // Update the import status based on the API response
              const updates: Partial<NxtImportItem> = {};

              if (
                statusItem.status === 'imported' ||
                statusItem.status === 'imported-update'
              ) {
                updates.isProcessing = false;
                updates.isReady = true;
                updates.uuid = statusItem.asset_uuid;
                notify(
                  statusItem.status === 'imported'
                    ? t('blox.batch_import.notifications.notify_import_complete', {
                        fileName: truncateString(matchingImport.fileName || 'item', 45),
                      })
                    : t('blox.batch_import.notifications.notify_import_update_complete', {
                        fileName: truncateString(matchingImport.fileName || 'item', 45),
                      }),
                  { type: 'success' },
                );
              } else if (
                ['import-failed', 'error', null].includes(statusItem.status ?? null)
              ) {
                updates.isProcessing = false;
                updates.isFailed = true;
                notify(
                  t('blox.batch_import.notifications.notify_import_failed', {
                    fileName: truncateString(matchingImport.fileName || 'item', 45),
                  }),
                  { type: 'error' },
                );
              }
              // For 'importing' status, keep current processing state

              // Apply updates if any
              if (Object.keys(updates).length > 0) {
                updateImport(matchingImportId, updates);
              }
            }
          });
        }
      });
    }
  }, [
    wireImportStatusData,
    siteKey,
    updateImport,
    notify,
    dataProvider,
    activeSite,
    t,
    betaProgramEnabled,
  ]);

  // Create stable query key for file imports
  const fileImportIds = useMemo(() => {
    return Object.values(imports)
      .filter((e) => e.importFile && !e.isReady && e.isProcessing)
      .map((e) => e.importId)
      .sort();
  }, [imports]);

  const { data: importFileStatusData } = useQuery({
    queryKey: ['importFileStatus', siteKey, fileImportIds],
    queryFn: async () => {
      // Filter imports that need status checking
      const importFileImports = Object.values(imports).filter(
        (e) => e.importFile && !e.isReady && e.isProcessing,
      );

      // Make one API call per channel with all object IDs for that channel
      const statuses = await Promise.all(
        Object.entries(importFileImports).map(async ([index, fileImport]) => {
          if (fileImport?.binaryId) {
            try {
              const response = await dataProvider.getImportStatus(fileImport.binaryId);
              const statusData = response?.data || null;
              // Return the status data for the channel
              return { importId: fileImport?.importId, statusData: statusData };
            } catch (error) {
              console.error(`Error fetching status for channel_id ${index}:`, error);
              return { importId: fileImport?.importId, statusData: null }; // Handle error gracefully
            }
          }
          return null; // Return null for items without binaryId
        }),
      );
      return statuses; // Return the array of statuses for all channels
    },
    // Enable query only if there are file imports that have a status of 'importing'
    enabled: fileImportIds.length > 0,
    refetchInterval: 5000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0, // Don't cache completed queries
    staleTime: 4000, // Consider data stale after 4s (before next refetch)
  });

  useEffect(() => {
    if (importFileStatusData) {
      // Get the current imports from storage to avoid dependency on imports state
      const currentImports = getNxtImportStorage()?.[siteKey]?.imports ?? {};
      // Update imports based on the wire import status data
      importFileStatusData.forEach((item) => {
        if (item && item.statusData) {
          const { importId, statusData } = item;
          // Update the import status based on the API response
          const updates: Partial<NxtImportItem> = {};
          const importData = currentImports[importId];

          if (statusData.complete) {
            updates.isProcessing = false;
            updates.isQueued = false;
            updates.isFailed = false;
            updates.isReady = true;

            if (betaProgramEnabled && !importData.inlineAsset) {
              dataProvider.createUserNotification({
                title: t('blox.batch_import.notifications.bpm.import_success_title'),
                body: t('blox.batch_import.notifications.bpm.import_success', {
                  fileName: truncateString(importData?.fileName || 'item', 25),
                }),
                link: {
                  url: `${window.location.origin}/${activeSite}/editorial/search`,
                  title: t('blox.batch_import.notifications.bpm.import_success_link'),
                },
              });
            } else {
              notify(
                t('blox.batch_import.notifications.notify_import_complete', {
                  fileName: truncateString(importData?.fileName || 'item', 45),
                }),
                { type: 'success' },
              );
            }
          } else if (statusData.queued || statusData.running) {
            updates.isProcessing = true;
            updates.isReady = false;
          }

          // Apply updates if any
          if (Object.keys(updates).length > 0) {
            updateImport(importId, updates);
          }
        }
      });
    }
  }, [
    importFileStatusData,
    siteKey,
    updateImport,
    dataProvider,
    activeSite,
    t,
    betaProgramEnabled,
    notify,
  ]);

  // -----------------------------------------------------------------
  // File Upload Mutation (supports concurrent uploads per file)
  // -----------------------------------------------------------------

  // The mutation can take one of two paths depending on whether the file
  // is a VMS-routed video. The result is discriminated so onSuccess can
  // pick the correct continuation.
  type VmsAssetResult = {
    _kind: 'vms';
    status: string;
    data?: FullEditorialAsset;
  };
  type BinaryUploadResult = PostCoreUploadResult & { _kind: 'binary' };
  type UploadMutationResult = VmsAssetResult | BinaryUploadResult;

  const fileBinaryUploadMutation = useMutation<
    UploadMutationResult,
    ApiErrorResponse,
    { importId: string; file: File; groupId?: string; inlineAsset?: boolean }
  >({
    mutationKey: ['fileBinaryUpload'],
    mutationFn: async ({
      importId,
      file,
      inlineAsset: _inlineAsset,
    }: {
      importId: string;
      file: File;
      groupId?: string;
      inlineAsset?: boolean;
    }): Promise<UploadMutationResult> => {
      // Create an AbortController for this specific upload
      const abortController = new AbortController();

      // Store the AbortController with the file
      updateImport(importId, { abortController: abortController });

      try {
        // For VMS-enabled sites uploading a video, route the file through
        // Field59 (S3 + create-asset-with-vms_upload_url) instead of the
        // BLOX binary endpoint. The binary endpoint always produces a
        // local video, and vms_upload_url is not patchable post-creation.
        if (hasField59 && isVideoFile(file)) {
          const vmsResult = await uploadVideoToVms({
            file,
            signal: abortController.signal,
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              if (progressEvent.loaded && progressEvent.total) {
                const percent = Math.round(
                  (progressEvent.loaded / progressEvent.total) * 100,
                );
                updateImport(importId, { progress: percent });
              }
            },
          });

          const createResult = await dataProvider.create('editorial', {
            data: {
              type: 'video',
              title: file.name,
              draft: true,
              vms_upload_url: vmsResult.vms_upload_url,
            },
          });

          return {
            _kind: 'vms',
            status: String(createResult?.status ?? ''),
            data: (createResult?.data as { data?: FullEditorialAsset })?.data,
          };
        }

        // Standard binary upload path for non-VMS or non-video files.
        const response = await dataProvider.postCoreUpload({
          file: file,
          axiosReqConfig: {
            signal: abortController.signal, // Add the abort signal to the request
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              if (progressEvent.loaded && progressEvent.total) {
                const percent = Math.round(
                  (progressEvent.loaded / progressEvent.total) * 100,
                );
                updateImport(importId, { progress: percent });
              }
            },
          },
        });

        return { ...response, _kind: 'binary' };
      } catch (error) {
        const castError = error as ApiErrorResponse;
        if (castError?.axiosCode === 'ERR_CANCELED') {
          throw new Error('Upload canceled by user');
        }
        throw error;
      }
    },
    onMutate: ({ importId }) => {
      updateImport(importId, { isQueued: false, isUploading: true, progress: 0 });
    },
    onSuccess: async (
      data: UploadMutationResult,
      { importId, file, groupId }: { importId: string; file: File; groupId?: string },
    ) => {
      // Decrement active upload counter.
      activeUploadCountRef.current = Math.max(0, activeUploadCountRef.current - 1);
      // Get the current imports from storage to avoid dependency on imports state
      const currentImports = getNxtImportStorage()?.[siteKey]?.imports ?? {};
      const importItem = currentImports[importId];

      // VMS path: asset was already created with vms_upload_url during the
      // mutation. Apply preset patch operations as a follow-up and notify.
      if (data._kind === 'vms') {
        const assetUuid = data.data?.uuid;
        if (data.status !== 'success' || !assetUuid) {
          if (betaProgramEnabled && !importItem?.inlineAsset) {
            dataProvider.createUserNotification({
              title: t('blox.batch_import.notifications.single_upload_failed_title'),
              body: t('blox.batch_import.notifications.single_upload_failed_body', {
                errorMessage: 'Failed to create VMS video asset.',
              }),
            });
          } else {
            notify(t('blox.batch_import.notifications.notify_missing_binary_id'), {
              type: 'error',
            });
          }
          updateImport(importId, {
            isUploading: false,
            isProcessing: false,
            isFailed: true,
          });
          return;
        }

        updateImport(importId, {
          uuid: assetUuid,
          isUploading: false,
          isProcessing: true,
        });

        try {
          const patchOperations: PatchType[] = createPatchOperationsPayload(
            batchUploadPresetsForm.getValues(),
          );
          if (groupId) {
            patchOperations.push({
              path: '/keywords',
              op: 'add',
              value: [`#nxt-import-group-${groupId}`],
            });
          }

          //Force all uploaded inline assets to be published, since they are not visible in the asset library otherwise.
          if (importItem?.inlineAsset) {
            const filteredPatchOperations = patchOperations.filter(
              (op) => op.path !== '/published' && op.path !== '/draft',
            );
            patchOperations.length = 0;
            patchOperations.push(
              ...filteredPatchOperations,
              { path: '/published', op: 'add', value: true },
              { path: '/draft', op: 'add', value: false },
            );
          }

          let finalAsset: FullEditorialAsset | undefined = data.data;
          if (patchOperations.length > 0) {
            const patchResult = await dataProvider.patchEditorialAsset({
              assetId: assetUuid,
              patchList: patchOperations,
            });
            if (patchResult?.data) {
              finalAsset = patchResult.data;
            }
          }

          const isGroupMember =
            !!getNxtImportStorage()?.[siteKey]?.imports[importId]?.groupId;
          if (!isGroupMember) {
            const displayName = truncateString(finalAsset?.title || file.name, 25);
            if (betaProgramEnabled && !importItem?.inlineAsset) {
              dataProvider.createUserNotification({
                title: t('blox.batch_import.notifications.single_upload_title'),
                body: t('blox.batch_import.notifications.single_upload_body', {
                  fileName: displayName,
                }),
                link: {
                  url: `${window.location.origin}/${activeSite}/editorial/edit/${assetUuid}`,
                  title: t('blox.batch_import.notifications.single_upload_link', {
                    fileName: displayName,
                  }),
                },
              });
            } else {
              notify(t('blox.batch_import.notifications.single_success'), {
                type: 'success',
              });
            }
          }

          updateImport(importId, {
            notificationSent: !isGroupMember,
            isUploading: false,
            isProcessing: false,
            isReady: true,
            fileName: finalAsset?.title,
            uuid: assetUuid,
            type: finalAsset?.type,
            data: finalAsset,
          });
        } catch {
          // Asset was created but presets couldn't be applied. Mark ready so
          // the user still sees the imported video; preset failure is a
          // soft error and the user can apply settings manually.
          updateImport(importId, {
            isUploading: false,
            isProcessing: false,
            isReady: true,
            uuid: assetUuid,
            type: data.data?.type,
            fileName: data.data?.title,
            data: data.data,
          });
        }
        return;
      }

      if (data.status === 'success') {
        // This is the response binary ID
        const binaryId = data?.data?.id;
        if (!binaryId) {
          if (betaProgramEnabled && !importItem?.inlineAsset) {
            dataProvider.createUserNotification({
              title: t('blox.batch_import.notifications.single_upload_failed_title'),
              body: t('blox.batch_import.notifications.single_upload_failed_body', {
                errorMessage: 'Missing binary ID in response.',
              }),
            });
          } else {
            notify(t('blox.batch_import.notifications.notify_missing_binary_id'), {
              type: 'error',
            });
          }
          updateImport(importId, {
            isUploading: false,
            isProcessing: false,
            isFailed: true,
          });
          return;
        }

        // Update import to processing state
        updateImport(importId, {
          binaryId: binaryId,
          isUploading: false,
          isProcessing: true,
        });

        try {
          if (getIsBpmFile(file)) {
            // For BPM files, we import them directly without applying presets.
            const importResult = await dataProvider.createImportTask({
              body: {
                fileId: binaryId,
              },
            });
            if (importResult?.status === 'success') {
              // Only notify individually if this file is not part of a group;
              // group members get a single summary notification instead.
              const isGroupMember =
                !!getNxtImportStorage()?.[siteKey]?.imports[importId]?.groupId;
              if (!isGroupMember) {
                if (betaProgramEnabled && !importItem?.inlineAsset) {
                  dataProvider.createUserNotification({
                    title: t('blox.batch_import.notifications.bpm.success_title'),
                    body: t('blox.batch_import.notifications.bpm.success'),
                  });
                } else {
                  notify(t('blox.batch_import.notifications.bpm.success'), {
                    type: 'success',
                  });
                }
              }

              // BPM file imports have their own import tracking ID
              // so the original binaryId is replaced
              updateImport(importId, {
                notificationSent: true,
                binaryId: importResult.data?.id,
                isProcessing: importResult.data?.queued || importResult.data?.running,
                isReady: importResult.data?.complete,
                data: importResult.data,
              });
            } else {
              // BPM import failed.
              if (betaProgramEnabled && !importItem?.inlineAsset) {
                dataProvider.createUserNotification({
                  title: t('blox.batch_import.notifications.bpm.error_title'),
                  body: t('blox.batch_import.notifications.bpm.error'),
                });
              } else {
                notify(t('blox.batch_import.notifications.bpm.error'), { type: 'error' });
              }
              updateImport(importId, {
                isUploading: false,
                isProcessing: false,
                isFailed: true,
              });
            }

            return; // Always return for BPM files, never apply preset patch operations.
          }

          // Apply presets only for non-BPM files.
          const patchOperations: PatchType[] = createPatchOperationsPayload(
            batchUploadPresetsForm.getValues(),
          );

          if (groupId) {
            patchOperations.push({
              path: '/keywords',
              op: 'add',
              value: [`#nxt-import-group-${groupId}`],
            });
          }

          //Force all uploaded inline assets to be published, since they are not visible in the asset library otherwise.
          if (importItem?.inlineAsset) {
            const filteredPatchOperations = patchOperations.filter(
              (op) => op.path !== '/published' && op.path !== '/draft',
            );
            patchOperations.length = 0;
            patchOperations.push(
              ...filteredPatchOperations,
              { path: '/published', op: 'add', value: true },
              { path: '/draft', op: 'add', value: false },
            );
          }

          // Use the real ID for the conversion
          const conversionResult = await dataProvider.binaryConvert({
            file: binaryId,
            patch: patchOperations,
          });

          if (conversionResult?.status === 'success') {
            // Only notify individually if this file is not part of a group;
            // group members get a single summary notification instead.
            const isGroupMember =
              !!getNxtImportStorage()?.[siteKey]?.imports[importId]?.groupId;
            if (!isGroupMember) {
              const displayName = truncateString(
                conversionResult?.data?.title || file.name,
                25,
              );
              if (betaProgramEnabled && !importItem?.inlineAsset) {
                dataProvider.createUserNotification({
                  title: t('blox.batch_import.notifications.single_upload_title'),
                  body: t('blox.batch_import.notifications.single_upload_body', {
                    fileName: displayName,
                  }),
                  link: {
                    url: `${window.location.origin}/${activeSite}/editorial/edit/${conversionResult?.data?.uuid}`,
                    title: t('blox.batch_import.notifications.single_upload_link', {
                      fileName: displayName,
                    }),
                  },
                });
              } else {
                notify(t('blox.batch_import.notifications.single_success'), {
                  type: 'success',
                });
              }
            }
            // Use real ID for final update
            updateImport(importId, {
              // Only mark notificationSent here for solo uploads; group members
              // have this flag set by the group-completion effect after it fires
              // its summary notification. Setting it here for group members would
              // cause the persistent guard in that effect to skip the notification.
              notificationSent: !isGroupMember,
              isUploading: false,
              isProcessing: false,
              isReady: true,
              fileName: conversionResult?.data?.title,
              uuid: conversionResult?.data?.uuid,
              type: conversionResult?.data?.type,
              data: conversionResult?.data,
            });
          } else {
            updateImport(importId, {
              isUploading: false,
              isProcessing: false,
              isFailed: true,
            });
          }
        } catch (error) {
          const castError = error as ApiErrorResponse;
          if (betaProgramEnabled && !importItem?.inlineAsset) {
            dataProvider.createUserNotification({
              title: t('blox.batch_import.notifications.single_upload_failed_title'),
              body: t('blox.batch_import.notifications.single_upload_failed_body', {
                errorMessage: castError?.message || '',
              }),
            });
          } else {
            notify(
              t('blox.batch_import.notifications.notify_upload_failed', {
                errorMessage: castError?.message,
              }),
              { type: 'error' },
            );
          }
          // Use real ID for error update
          updateImport(importId, {
            isUploading: false,
            isProcessing: false,
            isFailed: true,
            misc: { errorMessage: castError?.message },
          });
        }
      }
    },
    onError: (error, { importId, file, inlineAsset }) => {
      // Decrement active upload counter.
      activeUploadCountRef.current = Math.max(0, activeUploadCountRef.current - 1);
      const castError = error as ApiErrorResponse;
      if (
        castError?.axiosCode === 'ERR_CANCELED' ||
        castError?.message === 'Upload canceled by user'
      ) {
        notify(t('blox.batch_import.notifications.notify_upload_canceled'), {
          type: 'info',
        });
        return;
      } else {
        console.error('[NxtImport] upload: mutation error (postCoreUpload failed)', {
          importId,
          fileName: file.name,
          error: castError?.message,
          axiosCode: castError?.axiosCode,
        });
      }
      // Use real ID for error update
      updateImport(importId, {
        isUploading: false,
        isProcessing: false,
        isFailed: true,
        misc: { errorMessage: castError?.message },
      });

      if (betaProgramEnabled && !inlineAsset) {
        dataProvider.createUserNotification({
          title: t('blox.batch_import.notifications.single_upload_failed_title'),
          body: t('blox.batch_import.notifications.single_upload_failed_body', {
            errorMessage: castError?.message || '',
          }),
        });
      } else {
        notify(
          t('blox.batch_import.notifications.notify_upload_failed', {
            errorMessage: castError?.message,
          }),
          { type: 'error' },
        );
      }
    },
  });

  const handleFileChange = useCallback(
    (acceptedFiles: File[]) => {
      // When more than one file is dropped/selected together, bind them
      // into a group so a single summary notification fires on completion.
      const groupId = acceptedFiles.length > 1 ? uuidv4() : undefined;

      // Add the import(s).
      acceptedFiles.forEach((file) => {
        const importId = uuidv4();
        addImport(importId, 'editorial', file, undefined, groupId);
      });

      // Collapse the presets accordion when files are added.
      setAccordionExpanded(false);
    },
    [addImport],
  );

  // -----------------------------------------------------------------
  // Track active mutations using useMutationState and manual counter.
  // -----------------------------------------------------------------

  const pendingUploads = useMutationState({
    filters: { mutationKey: ['fileBinaryUpload'], status: 'pending' },
    select: (mutation) => mutation.state.variables,
  });

  // Use a ref to track active uploads more reliably than useMutationState alone.
  const activeUploadCountRef = useRef(0);
  const activeUploadCount = Math.max(pendingUploads.length, activeUploadCountRef.current);

  // -----------------------------------------------------------------
  // Queue Processing - Auto-start uploads based on MAX_CONCURRENT_UPLOADS.
  // -----------------------------------------------------------------

  // Track which imports have been started to prevent duplicate mutations.
  const startedImportsRef = useRef<Set<string>>(new Set());

  // Track group IDs for which the summary notification has already fired.
  // In-memory only — intentionally resets on page load.
  const notifiedGroupsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Don't process queue if we're at capacity.
    if (activeUploadCount >= MAX_CONCURRENT_UPLOADS) {
      return;
    }

    // Get queued files that aren't currently uploading and haven't been started yet.
    // Only process imports that belong to THIS tab (runtimeId match) to prevent
    // duplicate uploads when multiple tabs share localStorage via sync: true.
    const queuedFiles = Object.values(imports).filter(
      (item) =>
        item.isQueued &&
        !item.isUploading &&
        !item.isFailed &&
        !item.isCanceled &&
        item.resourceType === 'editorial' &&
        item.file &&
        item.runtimeId === runtimeId &&
        !startedImportsRef.current.has(item.importId), // Not already started.
    );

    // If no files to process, return early.
    if (queuedFiles.length === 0) {
      return;
    }

    // Calculate how many uploads we can start.
    const availableSlots = MAX_CONCURRENT_UPLOADS - activeUploadCount;
    const filesToStart = queuedFiles.slice(0, availableSlots);

    // Start uploads for available slots.
    filesToStart.forEach((item) => {
      // Mark as started immediately to prevent duplicate mutations.
      startedImportsRef.current.add(item.importId);
      // Increment the ref counter.
      activeUploadCountRef.current += 1;

      fileBinaryUploadMutation.mutate({
        importId: item.importId,
        file: item.file!,
        groupId: item.groupId,
        inlineAsset: item.inlineAsset,
      });
    });
  }, [imports, activeUploadCount, runtimeId, fileBinaryUploadMutation]);

  // Clean up started imports set when imports are removed.
  useEffect(() => {
    const currentImportIds = new Set(Object.keys(imports));
    const startedIds = Array.from(startedImportsRef.current);

    // Remove started IDs that are no longer in imports.
    startedIds.forEach((id) => {
      if (!currentImportIds.has(id)) {
        startedImportsRef.current.delete(id);
      }
    });

    // Remove notified group IDs whose every member has been cleared from imports.
    const activeGroupIds = new Set(
      Object.values(imports)
        .map((item) => item.groupId)
        .filter((gid): gid is string => !!gid),
    );
    notifiedGroupsRef.current.forEach((gid) => {
      if (!activeGroupIds.has(gid)) {
        notifiedGroupsRef.current.delete(gid);
      }
    });
  }, [imports]);

  // -----------------------------------------------------------------
  // Group-completion notification.
  // Fires one summary toast when every file in a group reaches a
  // terminal state (ready, failed, or canceled).
  // -----------------------------------------------------------------
  useEffect(() => {
    // Collect all items that belong to a group.
    const grouped = new Map<string, NxtImportItem[]>();
    Object.values(imports).forEach((item) => {
      if (!item.groupId) return;
      const group = grouped.get(item.groupId) ?? [];
      group.push(item);
      grouped.set(item.groupId, group);
    });

    grouped.forEach((items, groupId) => {
      // Fast in-session guard (resets on page reload).
      if (notifiedGroupsRef.current.has(groupId)) return;

      // Persistent guard: if any item in the group already has notificationSent
      // persisted in localStorage, the notification was sent in a prior session.
      if (items.some((item) => item.notificationSent)) {
        notifiedGroupsRef.current.add(groupId); // sync the in-memory ref
        return;
      }

      // All items must be terminal (not queued, uploading, or processing).
      const allTerminal = items.every(
        (item) => !item.isQueued && !item.isUploading && !item.isProcessing,
      );
      if (!allTerminal) return;

      const total = items.length;
      const successCount = items.filter((item) => item.isReady).length;
      const failedItems = items.filter((item) => item.isFailed || item.isCanceled);
      const failedCount = failedItems.length;

      let groupBody: string;
      if (failedCount === 0) {
        groupBody = t('blox.batch_import.notifications.group_complete_body_all_success', {
          total,
        });
      } else if (successCount === 0) {
        groupBody = t('blox.batch_import.notifications.group_complete_body_all_failed', {
          total,
        });
      } else {
        const failedFiles = failedItems
          .map((item) => truncateString(item.fileName || 'Unknown file', 30))
          .join(', ');
        groupBody = t('blox.batch_import.notifications.group_complete_body_partial', {
          success: successCount,
          total,
          failedFiles,
        });
      }
      if (betaProgramEnabled) {
        dataProvider.createUserNotification({
          title: t('blox.batch_import.notifications.group_complete_title'),
          body: groupBody,
          link: {
            url: `${window.location.origin}/${activeSite}/editorial/search?sort=start_time&dir=DESC&query=tag_keyword%3A"%23nxt-import-group-${groupId}"`,
            title: t('blox.batch_import.notifications.group_complete_link'),
          },
        });
      } else {
        const notifyType =
          successCount === 0 ? 'error' : successCount === total ? 'success' : 'warning';
        notify(groupBody, { type: notifyType });
      }

      // Mark all items in the group as notified so the flag persists across reloads.
      items.forEach((item) => {
        updateImport(item.importId, { notificationSent: true });
      });
      notifiedGroupsRef.current.add(groupId);
    });
  }, [imports, t, activeSite, dataProvider, updateImport, betaProgramEnabled, notify]);

  // -----------------------------------------------------------------
  // Batch group activation.
  // When all items in the currently-uploading batch group reach a
  // terminal state (ready, failed, or canceled), automatically activate
  // the next pending batch group so uploads continue sequentially.
  // -----------------------------------------------------------------
  useEffect(() => {
    const allPendingBatchItems = Object.values(imports).filter(
      (item) => item.isPendingBatch === true && item.batchGroupOrder !== undefined,
    );

    if (allPendingBatchItems.length === 0) return;

    // Find the lowest batchGroupOrder still waiting to start.
    const minPendingOrder = Math.min(
      ...allPendingBatchItems.map((i) => i.batchGroupOrder!),
    );

    // All items belonging to the batch that directly precedes the next pending batch.
    const precedingBatchItems = Object.values(imports).filter(
      (item) => item.batchGroupOrder === minPendingOrder - 1,
    );

    // The preceding batch is complete when every item has reached a terminal
    // state (not queued, uploading, processing, or still pending).
    const precedingBatchComplete =
      precedingBatchItems.length === 0 ||
      precedingBatchItems.every(
        (item) =>
          !item.isQueued &&
          !item.isUploading &&
          !item.isProcessing &&
          !item.isPendingBatch,
      );

    if (precedingBatchComplete) {
      // Release the next pending batch into the upload queue.
      allPendingBatchItems
        .filter((item) => item.batchGroupOrder === minPendingOrder)
        .forEach((item) => {
          updateImport(item.importId, { isPendingBatch: false, isQueued: true });
        });
    }
  }, [imports, updateImport]);

  // -----------------------------------------------------------------
  // Wire Import Handler.
  // -----------------------------------------------------------------

  const maybeWireImport = useCallback(
    async (
      record: any,
      channel_id?: string,
      channel_name?: string | null,
    ): Promise<string | boolean> => {
      const rowId = record?.id;
      const object_id = record?.object_id || rowId;

      if (!object_id) {
        console.error('object_id is missing.');
        return false; // Early return if object_id is missing
      }

      if (!channel_id) {
        console.error('channel_id is missing.');
        return false; // Return false if required parameters are missing
      }

      // Check if this wire import is already being tracked by looking for
      // syndication imports with matching object_id
      const existingImport = Object.values(imports).find(
        (importItem) =>
          importItem.resourceType === 'wire' &&
          (importItem as any).object_id === object_id,
      );

      if (existingImport) {
        if (record?.status === 'imported-update') {
          // Remove the existing import when status is imported-update
          removeImport(existingImport.importId);
        } else if ((existingImport as any).status === 'imported') {
          const route = `/${activeSite}/editorial/edit/${
            (existingImport as any).asset_uuid
          }/`;
          notify(t('blox.batch_import.notifications.notify_record_already_imported'), {
            type: 'info',
          });

          return route;
        } else {
          notify(t('blox.batch_import.notifications.notify_import_in_progress'), {
            type: 'info',
          });
          return true; // Import already in progress or completed
        }
      }

      // Check the current status of the record before proceeding
      const response = await dataProvider.getWireImportStatus(channel_id, [object_id]);
      const freshRecord = response?.data?.[0];

      if (freshRecord?.status === 'imported' && record?.status !== 'imported-update') {
        const route = `/${activeSite}/editorial/edit/${freshRecord?.asset_uuid}/`;
        notify(t('blox.batch_import.notifications.notify_record_already_imported'), {
          type: 'info',
        });
        return route;
      }

      try {
        // Call API to import the wire asset
        const importResponse = await dataProvider.importChannelAsset(
          channel_id,
          object_id,
        );

        if (importResponse?.status === 'success') {
          const responseData = importResponse?.data?.[0];
          const importId = `${channel_id}:${object_id}`;
          addImport(importId, 'wire');
          updateImport(importId, {
            fileName: record?.title,
            type: record?.type,
            misc: { channel_name: channel_name },
          });

          if (responseData?.status === 'imported') {
            if (betaProgramEnabled) {
              dataProvider.createUserNotification({
                title: t('blox.batch_import.notifications.wire_import_success_title'),
                body: t('blox.batch_import.notifications.wire_import_success_body', {
                  recordTitle: truncateString(record?.title || 'item', 25),
                }),
                link: {
                  url: `${window.location.origin}/${activeSite}/editorial/edit/${responseData.asset_uuid}/`,
                  title: t('blox.batch_import.notifications.wire_import_success_link', {
                    recordTitle: truncateString(record?.title || 'item', 25),
                  }),
                },
              });
            } else {
              notify(t('blox.batch_import.notifications.notify_record_imported'), {
                type: 'success',
              });
            }
            const route = `/${activeSite}/editorial/edit/${responseData.asset_uuid}/`;
            updateImport(importId, {
              isQueued: false,
              isUploading: false,
              isProcessing: false,
              isReady: true,
              isFailed: false,
              uuid: responseData?.asset_uuid || responseData?.uuid,
            });
            return route;
          }

          // Create a new import item for wire syndication tracking
          // We use a generated UUID for the importId since wire imports don't have file-based IDs
          // Update with wire-specific data
          updateImport(importId, {
            isQueued: false,
            isUploading: false,
            isProcessing: responseData?.status === 'importing',
            isReady: false,
            isFailed:
              responseData?.status !== 'imported' && responseData?.status !== 'importing',
            uuid: responseData?.asset_uuid || responseData?.uuid,
          });
          notify(t('blox.batch_import.notifications.notify_record_importing'), {
            type: 'info',
          });
          return true; // Return true if the import started successfully
        }
        console.error('Import failed:', importResponse);
        if (betaProgramEnabled) {
          dataProvider.createUserNotification({
            title: t('blox.batch_import.notifications.wire_import_failed_title'),
            body: t('blox.batch_import.notifications.wire_import_failed_body', {
              errorMessage: 'Unable to import wire asset.',
            }),
          });
        } else {
          notify(t('blox.batch_import.notifications.notify_wire_import_failed'), {
            type: 'error',
          });
        }
        return false; // Return false if the import failed
      } catch (error) {
        const castError = error as ApiErrorResponse;
        console.error('Error during import:', error);
        if (betaProgramEnabled) {
          dataProvider.createUserNotification({
            title: t('blox.batch_import.notifications.wire_import_failed_title'),
            body: t('blox.batch_import.notifications.wire_import_failed_body', {
              errorMessage: castError?.message || 'Unknown error',
            }),
          });
        } else {
          notify(
            t('blox.batch_import.notifications.notify_wire_import_error', {
              errorMessage: castError?.message || 'Unknown error',
            }),
            {
              type: 'error',
            },
          );
        }
        return false; // Return false in case of API failure
      }
    },
    [
      imports,
      addImport,
      removeImport,
      dataProvider,
      activeSite,
      notify,
      updateImport,
      t,
      betaProgramEnabled,
    ],
  );

  // -----------------------------------------------------------------
  // Drag and Drop Functions.
  // -----------------------------------------------------------------

  const { getRootProps, getInputProps } = useDropzone({
    onDragOver: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDrop: handleFileChange,
  });

  // -----------------------------------------------------------------
  // Intercept navigation and warn if uploads are in progress.
  // -----------------------------------------------------------------

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasOngoingUploads =
        Object.values(imports).filter((e) => e.isQueued || e.isUploading).length > 0;
      if (hasOngoingUploads) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [imports]);

  const contextValue: NxtImportContextType = useMemo(
    () => ({
      imports,
      addImport,
      updateImport,
      removeImport,
      cancelImport,
      clearAllImports,
      importCount,
      readyCount,
      hasActiveImports,
      hasReadyImports,
      allImportsReady,
      isDragActive,
      getRootProps,
      getInputProps,
      hasSyndicationImports,
      hasImportErrors,
      maybeWireImport,
      toggleImportModal,
      importModalOpen,
      // Presets form accordion.
      accordionExpanded,
      setAccordionExpanded,
    }),
    [
      imports,
      addImport,
      updateImport,
      removeImport,
      cancelImport,
      clearAllImports,
      importCount,
      readyCount,
      hasActiveImports,
      hasReadyImports,
      allImportsReady,
      isDragActive,
      getRootProps,
      getInputProps,
      hasSyndicationImports,
      hasImportErrors,
      maybeWireImport,
      toggleImportModal,
      importModalOpen,
      accordionExpanded,
      setAccordionExpanded,
    ],
  );

  return (
    <FormProvider
      reset={reset}
      {...batchUploadPresetsForm}
    >
      <NxtImportContext.Provider value={contextValue}>
        {children}
      </NxtImportContext.Provider>
    </FormProvider>
  );
});

NxtImportProvider.displayName = 'NxtImportProvider';
